import { DateTime } from 'luxon'
import { DateRange } from '~/types'
import { getWillieSupabaseClient } from '~/utils'
import _ from 'lodash'
import { DataSource, DataSourceSchema } from '@willie/schemas'
import { TablesInsert } from '@willie/supabase'

type ParsedDataPoint<T> = {
  dateTime: DateTime
  value: T
}

type RawDataPoint = {
  payload: any
  created_at: string
}

type ListDataPointsPaylaod = {
  orgID: string
  deviceID: string
  mappingQuery?: string
  limit?: number
  numbersOnly?: boolean
} & Partial<DateRange>

// TODO eventually use the backend api to consolidate this, event for raw datapoints
export async function listDatapointsPayloadByDeviceID<T = any>({
  orgID,
  deviceID,
  mappingQuery = 'payload -> decoded',
  startDate,
  endDate,
  limit,
  numbersOnly
}: ListDataPointsPaylaod): Promise<ParsedDataPoint<T>[]> {
  const supabase = getWillieSupabaseClient()
  let query = supabase
    .from('data-lake')
    .select(`payload:${mappingQuery}, created_at`)
    .eq('device_id', deviceID)
    .eq('org_id', orgID)
    .order('created_at', { ascending: true })

  if (startDate) {
    query = query.gte('created_at', startDate.toISO())
  }
  if (endDate) {
    query = query.lte('created_at', endDate.toISO())
  }
  if (limit) {
    query = query.limit(limit)
  }

  const { data: datapoints, error } = (await query) as any as {
    data: RawDataPoint[]
    error: any
  }

  if (error) {
    const message = `Error fetching data points for device ${deviceID}`

    console.log(message, error)
    throw new Error(message)
  }

  return (datapoints ?? [])
    .filter((dp) => (numbersOnly ? _.isNumber(dp.payload) : !_.isNil(dp.payload)))
    .map((dp) => {
      return {
        dateTime: dp.payload.ts ? DateTime.fromMillis(dp.payload.ts) : DateTime.fromISO(dp.created_at),
        value: dp.payload
      } satisfies ParsedDataPoint<T>
    })
}

export const createOrUpdateDatasource = async (
  dataSource: Omit<DataSource, 'id' | 'createdAt'> & { id?: string; createdAt?: string }
) => {
  const supabase = getWillieSupabaseClient()

  const payload: TablesInsert<'data_sources'> = {
    station_id: dataSource.stationID,
    source_type: dataSource.sourceType,
    unit: dataSource.unit,
    notes: dataSource.notes,
    config: dataSource.config,
    attachments: dataSource.attachments,
    reading_method: dataSource.readingMethod
  }

  if (dataSource.id) {
    payload.id = dataSource.id
  }

  const { data, error } = await supabase.from('data_sources').upsert(payload).select('*').single()

  if (error) {
    const message = `Error creating or updating physical meter`
    console.error(message, error)
    throw new Error(message)
  }
  return data!
}

export const getUnassignedDevices = async ({ orgID }: { orgID: string }) => {
  const supabase = getWillieSupabaseClient()
  const { data, error: error1 } = await supabase.from('orphan_devices').select('device_id').eq('org_id', orgID)

  if (error1 || !data) {
    console.error('Error fetching assigned devices', error1)
    return []
  }

  return data.filter((d): d is { device_id: string } => d !== null).map((d) => d.device_id)
}

type ListDataSourcesProps = {
  stationID?: string
  sourceType?: DataSource['sourceType']
}

export const listDataSources = async ({ stationID, sourceType }: ListDataSourcesProps) => {
  const supabase = getWillieSupabaseClient()
  const q = supabase.from('data_sources').select('*').order('created_at', { ascending: false })
  if (stationID) {
    q.eq('station_id', stationID)
  }
  if (sourceType) {
    q.eq('source_type', sourceType)
  }
  const { data: ds } = await q.throwOnError()

  if (!ds) {
    throw new Error('Error fetching stationd data sources')
  }

  return ds.map((d) =>
    DataSourceSchema.parse({
      id: d.id,
      unit: d.unit,
      notes: d.notes,
      config: d.config,
      createdAt: d.created_at,
      stationID: d.station_id,
      sourceType: d.source_type,
      attachments: d.attachments,
      readingMethod: d.reading_method
    })
  )
}

interface InsertManualPointsProps {
  datasourceID: string
  orgID: string
  datapoints: { date: DateTime; consumption: number; raw?: Record<string, any> }[]
}

export const insertManualDatapoints = async ({ datasourceID, orgID, datapoints }: InsertManualPointsProps) => {
  const supabase = getWillieSupabaseClient()
  const { error } = await supabase.from('data-lake').insert(
    datapoints.map<TablesInsert<'data-lake'>>((dp) => ({
      device_id: datasourceID,
      org_id: orgID,
      payload: { ts: dp.date.toMillis(), consumption: dp.consumption, raw: dp.raw ?? {} },
      created_at: dp.date.toISO()!
    }))
  )

  if (error) {
    console.error(`Error inserting manual datapoints for device ${datasourceID}`, error)
    throw new Error(`Error inserting manual datapoints for device ${datasourceID}`)
  }
}

interface DeleteManualPointsProps {
  datasourceID: string
  orgID: string
}

export const deleteManualDatapoints = async ({ datasourceID, orgID }: DeleteManualPointsProps) => {
  const supabase = getWillieSupabaseClient()
  const { error } = await supabase.from('data-lake').delete().eq('device_id', datasourceID).eq('org_id', orgID)

  if (error) {
    console.error(`Error deleting datapoints for device ${datasourceID}`, error)
    throw new Error(`Error deleting datapoints for device ${datasourceID}`)
  }
}
